import { Button, Popover } from '@mui/material'
import dayjs from 'dayjs'
import { useState } from 'react'
import Calendar from 'react-calendar'
import 'react-calendar/dist/Calendar.css'
import { DayJsDateSelectorCalendarStyleContainer } from './styledComponents'

const MomentDatePicker = ({
  date: dayjsDate,
  isDisabled = false,
  onChange,
  format = 'DD-MMM-YYYY',
  placeholder = 'Select a date...',
  extraHandlingAfterOnClick = () => {},
  buttonProps = {},
  ButtonComponent = Button,
  buttonTextOverride,
  calendarProps = {},
  clinicSchedules,
  selectedDate: selectedDateString,
}) => {
  const [anchorEl, setAnchorEl] = useState(null)

  // Open the popover by setting the anchor element (the button)
  const handleButtonClick = (event) => {
    setAnchorEl(event.currentTarget)
    extraHandlingAfterOnClick()
  }

  // Close the popover
  const handleClose = () => {
    setAnchorEl(null)
  }

  // When a date is selected in the calendar
  const handleDateSelect = (date) => {
    // Convert the selected Date object to a dayjs object
    const newMomentDate = dayjs(date)
    // Call the provided onChange callback
    onChange(newMomentDate)
    // Close the popover
    handleClose()
  }

  // Helper function to check if a date is an off day based on clinic schedule
  const isOffDay = (date) => {
    if (!clinicSchedules || !Array.isArray(clinicSchedules) || clinicSchedules.length === 0) {
      // If no schedule, check if it's Saturday (6) or Sunday (0)
      const dayOfWeek = dayjs(date).day()
      return dayOfWeek === 0 || dayOfWeek === 6
    }

    // Find the default schedule (or first schedule if no default)
    const defaultSchedule = clinicSchedules.find((s) => s.defaultSchedule) || clinicSchedules[0]
    if (!defaultSchedule?.weeklyHourMap) {
      // Fallback to Saturday/Sunday if schedule structure is invalid
      const dayOfWeek = dayjs(date).day()
      return dayOfWeek === 0 || dayOfWeek === 6
    }

    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
    const dayName = dayNames[dayjs(date).day()]
    const daySchedule = defaultSchedule.weeklyHourMap[dayName]

    // If day is not available or doesn't exist in schedule, it's an off day
    return !daySchedule || daySchedule.available === false
  }

  // Custom tile content function for styling
  const tileClassName = ({ date, view }) => {
    if (view !== 'month') return null

    const classes = []
    const dateMoment = dayjs(date)
    const todayMoment = dayjs()
    const selectedMoment = selectedDateString ? dayjs(selectedDateString, 'DD-MM-YYYY') : null

    // Check if date is in the past
    if (dateMoment.isBefore(todayMoment, 'day')) {
      classes.push('react-calendar__tile--past')
    }

    // Check if date is today
    if (dateMoment.isSame(todayMoment, 'day')) {
      classes.push('react-calendar__tile--today')
    }

    // Check if date is selected
    if (selectedMoment && dateMoment.isSame(selectedMoment, 'day')) {
      classes.push('react-calendar__tile--selected')
    }

    // Check if date is an off day
    if (isOffDay(date)) {
      classes.push('react-calendar__tile--off-day')
    }

    return classes.join(' ')
  }

  // Determine if the popover is open
  const open = Boolean(anchorEl)
  const id = open ? 'dayjs-date-picker-popover' : undefined

  // Ensure we have a Date object for react-calendar
  const calendarValue = dayjsDate ? dayjs(dayjsDate).toDate() : new Date()

  return (
    <>
      <ButtonComponent
        disabled={isDisabled}
        variant="outlined"
        onClick={handleButtonClick}
        {...buttonProps}
      >
        {buttonTextOverride || (dayjsDate ? dayjs(dayjsDate).format(format) : placeholder)}
      </ButtonComponent>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        container={() => document.body}
        disablePortal={false}
        sx={{
          zIndex: 30000,
        }}
        PaperProps={{
          style: {
            zIndex: '30000 !important',
            backgroundColor: '#ffffff',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            borderRadius: '8px',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            overflow: 'visible',
            marginTop: '4px',
            position: 'relative',
          },
          sx: {
            zIndex: 30000,
          },
        }}
        slotProps={{
          root: {
            sx: {
              zIndex: 30000,
            },
          },
        }}
      >
        <DayJsDateSelectorCalendarStyleContainer>
          <Calendar
            onChange={handleDateSelect}
            value={calendarValue}
            tileClassName={tileClassName}
            {...calendarProps}
          />
        </DayJsDateSelectorCalendarStyleContainer>
      </Popover>
    </>
  )
}

export default MomentDatePicker
